// 132 §4 — LEGAL HOLIDAYS: SEEDED AND COMPUTED, NEVER FETCHED.
//
// RULED (Laurent, 24 Aug 2026), on the question "can holidays be fetched
// automatically": **NO RUNTIME FETCH.** The reasons are on the record and they
// are why this file computes rather than calls:
//   · register item #65 is the lesson — a reference table that silently ran dry
//     produced a wrong number on a money path, found five weeks later by a
//     lawyer reading a memo rather than by any instrument;
//   · a third-party API deciding whether a day is a working day is that same
//     failure class with an external dependency added;
//   · the STANDALONE LOCAL tier has no network at all;
//   · a new external egress is a security-register item nobody asked for.
//
// ── THE SHAPE: THE RULE IS SEEDED, THE DATE IS COMPUTED ─────────────────────
// A row per (country, holiday) carries a RULE, not a date. Ten rows cover
// Belgium for every year rather than ten rows per year — which is the same
// choice that makes #65 impossible to repeat by accident: a table of dates runs
// dry silently, a table of rules does not run dry at all. What CAN still run
// out is the window a rule declares itself valid for, and that is deliberate:
// see COVERAGE below.
//
// ── WHAT IS FENCED OFF, AND IT IS THE WHOLE POINT ───────────────────────────
// THIS IS PRESENTATIONAL DATA. Nothing here may compute, shift or validate a
// deadline. Belgian procedural law extends a term expiring on a Saturday,
// Sunday or legal holiday to the next working day (Art. 53 C.jud.), and the
// judicial vacation (1 July–31 August) affects certain terms. NEITHER is in
// scope, neither is implied by this data, and no caller of this module may read
// it to decide a date. It answers one question — "is this day drawn as a
// non-working day, and what is it called" — and it must keep answering only
// that one.
//
// PURE: no import, no fetch, no clock. The rules arrive as an argument.

/** A holiday's date, as a rule rather than a date. */
export type HolidayRule =
  /** the same calendar day every year (1 May) */
  | { type: "fixed"; month: number; day: number }
  /** N days from Easter Sunday (Easter Monday is +1) */
  | { type: "easter"; offset: number };

/** One seeded holiday, resolved into this module's vocabulary. */
export type HolidayDef = {
  /** the taxonomy code, stable and machine-facing */
  code: string;
  /** ISO 3166-1 alpha-2, upper case */
  country: string;
  /** the NAME, already resolved into the reader's language by the caller */
  name: string;
  rule: HolidayRule;
  /** first year the rule applies (inclusive) */
  from: number;
  /** last year the rule is DECLARED for (inclusive). See COVERAGE. */
  to: number;
};

export type HolidayHit = { code: string; country: string; name: string };

/**
 * THE THREE STATES, and the middle one is what #65's second lesson buys.
 *
 *   "off"        the firm observes no country. Nothing is drawn and nothing is
 *                wrong: an empty list is a legitimate answer (§5).
 *   "covered"    the seeded rules reach this year.
 *   "uncovered"  the firm observes countries and the rules do NOT reach this
 *                year — the table has run out, or was never seeded, or the year
 *                is outside every rule's declared window.
 *
 * "uncovered" is never collapsed into "no holidays". A surface that cannot tell
 * "this year has no holidays" from "I do not know this year" is exactly the
 * surface #65 was found on.
 */
export type HolidayYearState = "off" | "covered" | "uncovered";

export type HolidayYear = {
  state: HolidayYearState;
  /** the observed set's first covered year, null when there is nothing to say */
  from: number | null;
  /** …and its last */
  to: number | null;
  /** the countries asked about, upper-cased and de-duplicated */
  countries: string[];
  /** `YYYY-MM-DD` → the holidays falling on it. Empty unless state === "covered". */
  byIso: Record<string, HolidayHit[]>;
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad2(m)}-${pad2(d)}`;

/**
 * Easter Sunday, Gregorian — the Meeus/Jones/Butcher algorithm.
 *
 * Pure integer arithmetic, no Date, valid for any Gregorian year. Returns
 * 1-based month and day. This is the ONLY thing in the calendar work that is
 * computed rather than stored, because the alternative is a table of Easter
 * dates, which is a table that runs dry.
 */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  return { month: Math.floor(n / 31), day: (n % 31) + 1 };
}

/**
 * A rule's ISO date in one year, or null when the rule does not apply to it.
 *
 * UTC ARITHMETIC THROUGHOUT. An Easter offset is day arithmetic and must not
 * pass through a local-time Date: on a DST boundary a local `+1 day` is 23 or
 * 25 hours and lands on the wrong date. `Date.UTC` has no such boundary.
 */
export function holidayDateIn(rule: HolidayRule, year: number): string | null {
  if (rule.type === "fixed") {
    if (rule.month < 1 || rule.month > 12 || rule.day < 1 || rule.day > 31) return null;
    return iso(year, rule.month, rule.day);
  }
  if (rule.type === "easter") {
    const e = easterSunday(year);
    const t = new Date(Date.UTC(year, e.month - 1, e.day + rule.offset));
    return iso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
  }
  return null;
}

/** A `meta` blob off a seeded row, parsed into a rule — or null if it is not one. */
function ruleOf(meta: unknown): HolidayRule | null {
  const r = (meta as { rule?: unknown } | null)?.rule as Record<string, unknown> | undefined;
  if (!r || typeof r !== "object") return null;
  if (r.type === "fixed" && typeof r.month === "number" && typeof r.day === "number") {
    return { type: "fixed", month: r.month, day: r.day };
  }
  if (r.type === "easter" && typeof r.offset === "number") {
    return { type: "easter", offset: r.offset };
  }
  return null;
}

/**
 * Seeded `TaxonomyEntry` rows of kind `legal_holiday` → this module's defs.
 *
 * `label` is the caller's job: it passes a resolver so the NAME comes out in
 * the reader's language through the same `pickEntryLabel` cascade every other
 * taxonomy uses. §9 — holiday names are DATA, carried by the seed in en/fr/nl,
 * not UI strings with their own keys.
 *
 * An archived row is dropped: that is how a firm says it does not observe one,
 * without a schema change and without editing law.
 */
export function holidayDefsFrom(
  entries: readonly unknown[] | null | undefined,
  label: (entry: unknown) => string,
): HolidayDef[] {
  const out: HolidayDef[] = [];
  for (const raw of entries || []) {
    const e = raw as {
      code?: unknown;
      archived?: unknown;
      meta?: { country?: unknown; from?: unknown; to?: unknown } | null;
    };
    if (!e || e.archived === true) continue;
    const rule = ruleOf(e.meta);
    const country = typeof e.meta?.country === "string" ? e.meta.country.toUpperCase() : "";
    const from = typeof e.meta?.from === "number" ? e.meta.from : null;
    const to = typeof e.meta?.to === "number" ? e.meta.to : null;
    if (!rule || !country || from == null || to == null) continue;
    out.push({ code: String(e.code || ""), country, name: label(raw), rule, from, to });
  }
  return out;
}

/**
 * The holidays of one year, for the countries a firm observes.
 *
 * `countries` is the firm's setting (§5). An empty list is "off" and is not an
 * error. A non-empty list with no rule reaching `year` is "uncovered", which
 * the caller must be able to say out loud.
 */
export function resolveHolidayYear(
  defs: readonly HolidayDef[],
  countries: readonly string[] | null | undefined,
  year: number,
): HolidayYear {
  const want = Array.from(
    new Set((countries || []).map((c) => String(c || "").toUpperCase()).filter(Boolean)),
  );
  if (!want.length) return { state: "off", from: null, to: null, countries: [], byIso: {} };

  const mine = defs.filter((d) => want.includes(d.country));
  if (!mine.length) return { state: "uncovered", from: null, to: null, countries: want, byIso: {} };

  // THE OBSERVED SET'S WINDOW IS ITS NARROWEST MEMBER'S, not its widest. A year
  // one observed country covers and another does not is a year this firm's
  // calendar would draw half of, and half a holiday set is worse than none:
  // it looks exactly like a year with fewer holidays.
  const from = Math.max(...mine.map((d) => d.from));
  const to = Math.min(...mine.map((d) => d.to));
  if (year < from || year > to) {
    return { state: "uncovered", from, to, countries: want, byIso: {} };
  }

  const byIso: Record<string, HolidayHit[]> = {};
  for (const d of mine) {
    if (year < d.from || year > d.to) continue;
    const on = holidayDateIn(d.rule, year);
    if (!on) continue;
    (byIso[on] ||= []).push({ code: d.code, country: d.country, name: d.name });
  }
  return { state: "covered", from, to, countries: want, byIso };
}

/**
 * The holidays a firm observes across a RANGE of ISO dates — what a 14-day
 * strip needs, since a strip can straddle 31 December.
 *
 * Returns the same three states. `uncovered` if ANY year the range touches is
 * uncovered, because a strip that drew ten days of a covered year and four of
 * an unknown one would be lying about the four.
 */
export function resolveHolidaysForDates(
  defs: readonly HolidayDef[],
  countries: readonly string[] | null | undefined,
  isoDates: readonly string[],
): HolidayYear {
  const years = Array.from(new Set(isoDates.map((s) => Number(String(s).slice(0, 4))))).filter(
    (y) => Number.isFinite(y) && y > 0,
  );
  if (!years.length) {
    return resolveHolidayYear(defs, countries, new Date().getUTCFullYear());
  }
  const parts = years.map((y) => resolveHolidayYear(defs, countries, y));
  if (parts.some((p) => p.state === "off")) return parts[0];
  const worst = parts.find((p) => p.state === "uncovered");
  if (worst) return worst;
  const byIso: Record<string, HolidayHit[]> = {};
  for (const p of parts) for (const [k, v] of Object.entries(p.byIso)) byIso[k] = v;
  return {
    state: "covered",
    from: Math.max(...parts.map((p) => p.from ?? -Infinity)),
    to: Math.min(...parts.map((p) => p.to ?? Infinity)),
    countries: parts[0].countries,
    byIso,
  };
}

/** The holidays on one ISO date, or an empty array. Never throws on a miss. */
export const holidaysOn = (res: HolidayYear | null | undefined, isoDate: string): HolidayHit[] =>
  (res && res.state === "covered" && res.byIso[isoDate]) || [];

/**
 * 132 §6/§7 — ONE LOOKUP, HANDED TO EVERY SURFACE THAT DRAWS A DAY.
 *
 * The surfaces do not share a window: the two 14-day strips draw from today,
 * and the agenda navigates to any month of any year. So the resolution cannot
 * be a fixed range computed once in App — it has to be a question a surface can
 * ask about the day it is drawing.
 *
 * Per-year memoised, because a 14-day strip asks fourteen times and a month
 * grid up to forty-two, for one or two years. The cache is a closure and the
 * inputs are frozen at construction, so a caller `useMemo`s this on
 * (defs, countries) and every surface below gets the same object.
 *
 * PRESENTATIONAL, and the fence is the reason this returns names and nothing
 * else: no caller may read it to decide a term, a deadline or a date shift.
 */
export type HolidayLookup = {
  /** the year's full answer, including its state */
  year: (y: number) => HolidayYear;
  /** `off` | `covered` | `uncovered`, for the year that ISO date falls in */
  stateOn: (isoDate: string) => HolidayYearState;
  /** the holidays on that date — always [] unless that year is covered */
  on: (isoDate: string) => HolidayHit[];
  /** the observed country codes, for a surface that wants to say so */
  countries: string[];
};

export function makeHolidayLookup(
  defs: readonly HolidayDef[],
  countries: readonly string[] | null | undefined,
): HolidayLookup {
  const cache = new Map<number, HolidayYear>();
  const year = (y: number): HolidayYear => {
    let hit = cache.get(y);
    if (!hit) {
      hit = resolveHolidayYear(defs, countries, y);
      cache.set(y, hit);
    }
    return hit;
  };
  const yearOfIso = (isoDate: string) => Number(String(isoDate).slice(0, 4));
  return {
    year,
    stateOn: (isoDate) => year(yearOfIso(isoDate)).state,
    on: (isoDate) => holidaysOn(year(yearOfIso(isoDate)), isoDate),
    countries: Array.from(
      new Set((countries || []).map((c) => String(c || "").toUpperCase()).filter(Boolean)),
    ),
  };
}

/**
 * 132 §5 — WHICH COUNTRIES THERE IS ANYTHING TO OBSERVE, in seed order.
 *
 * The offer list is derived from the seeded rules, NOT from the ISO country
 * taxonomy. §4 ships Belgium only, so an ISO list would offer ~250 countries of
 * which 249 draw nothing — a control that mostly does nothing, and one that
 * cannot tell a firm why. This grows by itself as the seed grows, which is
 * exactly how §4 says another country arrives ("rows, added later by the same
 * seed"). An empty result means the seed has not run, and the panel says so.
 */
export function availableHolidayCountries(
  entries: readonly unknown[] | null | undefined,
): string[] {
  const out: string[] = [];
  for (const raw of entries || []) {
    const c = (raw as { meta?: { country?: unknown } } | null)?.meta?.country;
    const code = typeof c === "string" ? c.toUpperCase() : "";
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
}

/**
 * 132 §5 — WHICH COUNTRIES THE FIRM OBSERVES.
 *
 * `FirmSettings.holidayCountries` is an array of ISO alpha-2 codes, and NULL IS
 * NOT []: null means the firm has never said, so the default is derived from
 * its own country; `[]` means "none, deliberately", and draws nothing.
 *
 * The firm's own country is a CODE of the `country` taxonomy (207 §12: picked
 * from the country list in Settings → Firm details, and a typed text converted
 * once by server/scripts/convert-firm-country.js), matched through that
 * taxonomy — the same rows the client-type and jurisdiction pickers use —
 * rather than by a name table written here: the matcher reads the code by its
 * code facet, and a text a tenant has not yet converted by its labels. `match` is the caller's `resolveTaxonomyEntry`, which is
 * the five-facet matcher 05m ruled the ONE matcher; passing it in keeps this
 * module pure.
 */
export function observedCountries(
  stored: unknown,
  firmCountry: unknown,
  match: (value: unknown) => { code?: unknown } | null,
): string[] {
  if (Array.isArray(stored)) {
    return Array.from(
      new Set(stored.map((c) => String(c || "").toUpperCase()).filter(Boolean)),
    );
  }
  const raw = String(firmCountry || "").trim();
  if (!raw) return [];
  const hit = match(raw);
  const code = hit && typeof hit.code === "string" ? hit.code.toUpperCase() : "";
  // A country the taxonomy cannot resolve yields NOTHING rather than a guess.
  // Drawing the wrong nation's holidays on a lawyer's calendar is worse than
  // drawing none, and §5 already rules that drawing none breaks nothing.
  return code && /^[A-Z]{2}$/.test(code) ? [code] : [];
}
